import clsx from 'clsx';
import {useEffect, useState} from 'react';
import {ActiveSiteBanner} from '@common/core/bootstrap-data/bootstrap-data';
import {useBootstrapData} from '@common/core/bootstrap-data/bootstrap-data-context';
import {IconButton} from '@common/ui/buttons/icon-button';
import {CloseIcon} from '@common/icons/material/Close';
import {
  getFromLocalStorage,
  setInLocalStorage,
} from '@common/utils/hooks/local-storage';
import {isAbsoluteUrl} from '@common/utils/urls/is-absolute-url';
import {Link} from 'react-router-dom';
import {useTrans} from '@common/i18n/use-trans';
import {message} from '@common/i18n/message';
import {SiteBannerColor} from '@app/admin/site-banners/site-banner';

function dismissStorageKey(bannerId: number, resetToken: number): string {
  return `site-banner-dismissed:${bannerId}:${resetToken}`;
}

const colorClasses: Record<SiteBannerColor, string> = {
  primary: 'border-primary/20 bg-primary/10 text-main',
  warning: 'border-warning/20 bg-warning/15 text-main',
  danger: 'border-danger/20 bg-danger/10 text-main',
  success: 'border-positive/20 bg-positive/10 text-main',
  neutral: 'border-divider bg-chip text-main',
};

const linkClasses: Record<SiteBannerColor, string> = {
  primary: 'text-primary',
  warning: 'text-warning',
  danger: 'text-danger',
  success: 'text-positive',
  neutral: 'text-primary',
};

function resolveColor(color: string | undefined): SiteBannerColor {
  if (
    color === 'primary' ||
    color === 'warning' ||
    color === 'danger' ||
    color === 'success' ||
    color === 'neutral'
  ) {
    return color;
  }
  return 'primary';
}

interface SiteBannerProps {
  className?: string;
}

/**
 * Полоска объявления между меню и контентом.
 * Добавлено: 2026-10-07.
 * Изменено: 2026-10-07 — цвет + reset_token для повторного показа после dismiss.
 */
export function SiteBanner({className}: SiteBannerProps) {
  const {trans} = useTrans();
  const {data} = useBootstrapData();
  const banner = (data?.active_site_banner ?? null) as ActiveSiteBanner | null;
  const resetToken = banner?.reset_token ?? 0;
  const storageKey =
    banner != null ? dismissStorageKey(banner.id, resetToken) : null;

  const [dismissedKey, setDismissedKey] = useState<string | null>(() => {
    if (!banner?.dismissible || !storageKey) {
      return null;
    }
    return getFromLocalStorage(storageKey, false) ? storageKey : null;
  });

  useEffect(() => {
    if (!banner?.dismissible || !storageKey) {
      setDismissedKey(null);
      return;
    }
    setDismissedKey(
      getFromLocalStorage(storageKey, false) ? storageKey : null,
    );
  }, [banner?.id, banner?.dismissible, storageKey]);

  if (!banner || (storageKey != null && dismissedKey === storageKey)) {
    return null;
  }

  const color = resolveColor(banner.color);
  const linkLabel = banner.link_label?.trim() || banner.link_url;
  const hasLink = !!banner.link_url?.trim();

  return (
    <div
      className={clsx(
        'relative flex w-full items-center justify-center gap-12 border-b px-40 py-10 text-center text-sm',
        colorClasses[color],
        className,
      )}
      role="status"
    >
      <div className="min-w-0">
        <span className="whitespace-pre-wrap break-words">{banner.message}</span>
        {hasLink ? (
          <>
            {' '}
            {isAbsoluteUrl(banner.link_url!) ? (
              <a
                href={banner.link_url!}
                target="_blank"
                rel="noopener noreferrer"
                className={clsx(
                  'font-medium underline',
                  linkClasses[color],
                )}
              >
                {linkLabel}
              </a>
            ) : (
              <Link
                to={banner.link_url!}
                className={clsx(
                  'font-medium underline',
                  linkClasses[color],
                )}
              >
                {linkLabel}
              </Link>
            )}
          </>
        ) : null}
      </div>
      {banner.dismissible ? (
        <IconButton
          size="xs"
          className="absolute right-8 top-1/2 -translate-y-1/2 text-muted"
          onClick={() => {
            if (!storageKey) {
              return;
            }
            setInLocalStorage(storageKey, true);
            setDismissedKey(storageKey);
          }}
          aria-label={trans(message('Dismiss'))}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </div>
  );
}
